import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Loader2,
  Bot,
  User,
  Sparkles,
  Database,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import * as api from '../services/api';

function MessageBubble({ message, isUser }) {
  const [showContext, setShowContext] = useState(false);

  if (isUser) {
    return (
      <div className="flex justify-end mb-4">
        <div className="max-w-[80%] bg-accent-purple rounded-2xl rounded-tr-sm px-4 py-3">
          <p className="text-sm text-white">{message.content}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start mb-4">
      <div className="max-w-[80%]">
        <div className="bg-dark-700 rounded-2xl rounded-tl-sm px-4 py-3 border border-dark-600">
          <p className="text-sm text-dark-100 whitespace-pre-wrap">{message.content}</p>
        </div>

        {/* Show retrieved context if available */}
        {message.context && message.context.length > 0 && (
          <div className="mt-2">
            <button
              onClick={() => setShowContext(!showContext)}
              className="flex items-center gap-2 text-xs text-dark-400 hover:text-dark-300 transition-colors"
            >
              <Database size={12} />
              <span>{message.context.length} sources used</span>
              {showContext ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>

            {showContext && (
              <div className="mt-2 space-y-2 animate-fadeIn">
                {message.context.map((ctx, i) => (
                  <div
                    key={i}
                    className="bg-dark-800 border border-dark-600 rounded-lg p-3 text-xs"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-accent-purple">{ctx.name}</span>
                      <span className="px-1.5 py-0.5 bg-dark-600 rounded text-dark-300">
                        {ctx.entity_type}
                      </span>
                      {ctx.score && (
                        <span className="text-dark-400">
                          {(ctx.score * 100).toFixed(0)}% match
                        </span>
                      )}
                    </div>
                    <p className="text-dark-300 line-clamp-2">{ctx.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AskAtlas() {
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: "Hello! I'm Atlas, your organizational intelligence assistant. Ask me anything about your services, teams, patterns, regulations, or how they're connected. I'll search the knowledge graph and provide informed answers.",
      context: null,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      // Call the RAG chat endpoint
      const response = await api.askAtlas(userMessage.content);

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.answer,
        context: response.context || [],
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      setError(err.message);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I'm sorry, I encountered an error while processing your question. Please try again.",
        context: null,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const suggestedQuestions = [
    "What services depend on authentication?",
    "Which teams own the payment services?",
    "What compliance regulations apply to data handling?",
    "How is the fraud detection system connected?",
    "What patterns are used for API security?",
  ];

  const handleSuggestion = (question) => {
    setInput(question);
    inputRef.current?.focus();
  };

  return (
    <div className="h-full flex flex-col bg-dark-900">
      {/* Header */}
      <div className="p-6 border-b border-dark-600 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-purple/20 rounded-xl flex items-center justify-center">
            <Sparkles size={20} className="text-accent-purple" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Ask Atlas</h1>
            <p className="text-sm text-dark-400">AI-powered organizational intelligence</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isUser={message.role === 'user'}
            />
          ))}

          {loading && (
            <div className="flex justify-start mb-4">
              <div className="bg-dark-700 rounded-2xl rounded-tl-sm px-4 py-3 border border-dark-600">
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-accent-purple" />
                  <span className="text-sm text-dark-300">Searching knowledge graph...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested questions when no user messages yet */}
        {messages.length === 1 && (
          <div className="max-w-3xl mx-auto mt-6">
            <p className="text-sm text-dark-400 mb-3">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((question, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestion(question)}
                  className="px-3 py-2 bg-dark-700 hover:bg-dark-600 border border-dark-600 rounded-lg text-sm text-dark-300 hover:text-white transition-colors text-left"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="px-6 pb-2">
          <div className="max-w-3xl mx-auto bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle size={16} className="text-red-400" />
            <span className="text-sm text-red-400">{error}</span>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-6 border-t border-dark-600 flex-shrink-0">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your organization's services, teams, and architecture..."
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-accent-purple transition-colors pr-12"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="px-4 py-3 bg-accent-purple hover:bg-accent-purple-dark disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
            >
              {loading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Send size={20} />
              )}
            </button>
          </div>
          <p className="text-xs text-dark-500 mt-2 text-center">
            Atlas searches your knowledge graph and uses AI to answer questions
          </p>
        </form>
      </div>
    </div>
  );
}

export default AskAtlas;
